/**
 * Select — React (web) implementation.
 *
 * The WAI-ARIA select-only combobox: a native button with `role="combobox"`
 * is the field shell, and a listbox of options opens below it, over the
 * content after the field. Focus stays on the button while the list is open;
 * the highlighted option is conveyed by `aria-activedescendant`, as the
 * Autocomplete does, so the focus ring stays on the field (as v7 draws Open).
 * Disabled stays focusable (aria-disabled) and never opens.
 * All styling references the shared constants in Select.ts.
 */

import React, { useEffect, useId, useRef, useState } from 'react';
import { ensureStyle, FOCUS_RING_OUTLINE } from '../../elements/focusStyle.web';
import { mouseHoverHandlers } from '../../elements/mouseHover.web';
import { FieldError } from '../FieldError/FieldError.web';
import { hasFieldErrorMessage } from '../FieldError/FieldError';
import { Icon } from '../Icon/Icon.web';
import {
  isSelectLabelFloating,
  selectColorsFor,
  selectInitialIndex,
  selectStepIndex,
  selectTypeaheadIndex,
  SELECT_BORDER_RADIUS,
  SELECT_BORDER_WIDTH,
  SELECT_CHEVRON_COLOR,
  SELECT_CHEVRON_ICON,
  SELECT_CHEVRON_SIZE,
  SELECT_ERROR_GAP,
  SELECT_ERROR_INDENT,
  SELECT_FLOATING_LABEL_TYPOGRAPHY,
  SELECT_HEIGHT,
  SELECT_HORIZONTAL_PADDING,
  SELECT_LABEL_VALUE_GAP,
  SELECT_OPTION_BACKGROUND,
  SELECT_OPTION_HIGHLIGHT_BACKGROUND,
  SELECT_OPTION_HORIZONTAL_PADDING,
  SELECT_OPTION_TEXT_COLOR,
  SELECT_OPTION_TYPOGRAPHY,
  SELECT_OPTION_VERTICAL_PADDING,
  SELECT_PANEL_BACKGROUND,
  SELECT_PANEL_BORDER_COLOR,
  SELECT_PANEL_BORDER_WIDTH,
  SELECT_PANEL_GAP,
  SELECT_PANEL_LAYER,
  SELECT_PANEL_RADIUS,
  SELECT_PANEL_SHADOW,
  SELECT_RESTING_LABEL_TYPOGRAPHY,
  SELECT_SHELL_GAP,
  SELECT_VALUE_TYPOGRAPHY,
} from './Select';
import type { SelectChoice } from './Select';
import type { TypeStyle } from '../../elements/typography';

// A long list (the 51 US states) shows six and a half rows and scrolls inside
// itself, so the page does not grow by the whole list and the half row hints
// that there is more.
const WEB_SELECT_VISIBLE_ROWS = 6.5;
const WEB_SELECT_OPTION_ROW_HEIGHT =
  SELECT_OPTION_TYPOGRAPHY.lineHeight + 2 * SELECT_OPTION_VERTICAL_PADDING;
const WEB_SELECT_PANEL_MAX_HEIGHT =
  WEB_SELECT_VISIBLE_ROWS * WEB_SELECT_OPTION_ROW_HEIGHT + 2 * SELECT_PANEL_BORDER_WIDTH;

const SELECT_FOCUS_STYLE_ID = 'galapagos-select-focus-visible';
const SELECT_FIELD_SELECTOR = '[data-galapagos-select-field="true"]';

// The button is the field shell, so the ring is drawn on it directly and only
// for keyboard focus (ADR-025 Rule 2). No outline is removed, so nothing needs
// a `:has()` fallback.
function ensureSelectFocusStyle(): void {
  ensureStyle(
    SELECT_FOCUS_STYLE_ID,
    `${SELECT_FIELD_SELECTOR}:focus-visible { ${FOCUS_RING_OUTLINE} }`,
  );
}

const PANEL_SHADOW =
  `${SELECT_PANEL_SHADOW.offset.width}px ${SELECT_PANEL_SHADOW.offset.height}px` +
  ` ${SELECT_PANEL_SHADOW.radius}px ${SELECT_PANEL_SHADOW.spread}px ${SELECT_PANEL_SHADOW.color}`;

function textStyle(type: TypeStyle, color: string): React.CSSProperties {
  return {
    fontFamily: type.fontFamily,
    fontWeight: type.fontWeight,
    fontSize: type.fontSize,
    lineHeight: `${type.lineHeight}px`,
    letterSpacing: type.letterSpacing,
    color,
  };
}

/** A single printable character typed without a modifier: a jump-to key. */
function isTypeaheadKey(event: React.KeyboardEvent): boolean {
  return (
    event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey
  );
}

export interface WebSelectProps {
  label?: string;
  options: readonly SelectChoice[];
  /** Controlled picked value. */
  value?: string;
  /** Initial picked value when uncontrolled. */
  defaultValue?: string;
  errorText?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  /** With a name, a hidden input carries the value so a plain form posts it. */
  name?: string;
  /** Domain action id for the change interaction (ADR-081 Rule 4). */
  actionId?: string;
  /** Action id for opening and closing the list, kept apart from a pick. */
  toggleActionId?: string;
  /** Receives the newly picked option's value. */
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  'data-testid'?: string;
  'aria-label'?: string;
}

export function Select({
  label,
  options,
  value,
  defaultValue,
  errorText,
  disabled = false,
  autoFocus = false,
  name,
  actionId = 'ui.select.change',
  toggleActionId = 'ui.select.toggle',
  onChange,
  onBlur,
  onFocus,
  'data-testid': testId,
  'aria-label': ariaLabel,
}: WebSelectProps) {
  ensureSelectFocusStyle();

  const generatedId = useId();
  const baseId = testId ? `${testId}-select` : `select-${generatedId}`;
  const labelId = `${baseId}-label`;
  const listboxId = `${baseId}-listbox`;
  const errorId = `${baseId}-error`;
  const optionId = (index: number) => `${listboxId}-option-${index}`;

  const [ownValue, setOwnValue] = useState(defaultValue);
  const currentValue = value !== undefined ? value : ownValue;
  const picked = options.find((option) => option.value === currentValue);

  const [listOpen, setOpen] = useState(false);
  // Disabling the field closes an open list, so nothing can be picked from it.
  const open = listOpen && !disabled;
  const [activeIndex, setActiveIndex] = useState(-1);
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const hasError = hasFieldErrorMessage(errorText);
  const floating = isSelectLabelFloating(picked !== undefined);
  const colors = selectColorsFor({
    disabled,
    hasError,
    focused: focused && !disabled,
    hovered: hovered && !disabled,
    open,
  });

  const openList = () => {
    if (disabled) return;
    setActiveIndex(selectInitialIndex(options, currentValue));
    setOpen(true);
  };

  const closeList = () => {
    setOpen(false);
    setActiveIndex(-1);
  };

  const pick = (index: number) => {
    if (disabled) return;
    const option = options[index];
    closeList();
    buttonRef.current?.focus();
    if (!option || option.value === currentValue) return;
    if (value === undefined) setOwnValue(option.value);
    onChange?.(option.value);
  };

  // Focus stays on the button, so the browser will not scroll the highlighted
  // option into view by itself.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    document
      .getElementById(`${listboxId}-option-${activeIndex}`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [open, activeIndex, listboxId]);

  // Disabling forgets an open list, so re-enabling does not bring it back.
  useEffect(() => {
    if (!disabled) return;
    setOpen(false);
    setActiveIndex(-1);
  }, [disabled]);

  // A press anywhere outside the field and its list closes the list unchanged.
  // Pointer events, because iOS Safari sends no mouse events for a tap on a
  // blank part of the page.
  useEffect(() => {
    if (!open) return undefined;
    const handlePress = (event: PointerEvent) => {
      if (event.target instanceof Node && rootRef.current?.contains(event.target)) return;
      setOpen(false);
      setActiveIndex(-1);
    };
    document.addEventListener('pointerdown', handlePress);
    return () => document.removeEventListener('pointerdown', handlePress);
  }, [open]);

  const handleKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = (event) => {
    if (disabled) {
      // Enter and Space would otherwise click the button.
      if (event.key === 'Enter' || event.key === ' ') event.preventDefault();
      return;
    }
    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        openList();
      }
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((index) => selectStepIndex(options.length, index, 1));
        return;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((index) => selectStepIndex(options.length, index, -1));
        return;
      case 'Home':
        event.preventDefault();
        setActiveIndex(options.length > 0 ? 0 : -1);
        return;
      case 'End':
        event.preventDefault();
        setActiveIndex(options.length - 1);
        return;
      case 'Enter':
      case ' ': {
        event.preventDefault();
        // A real click on the highlighted option, so a keyboard pick reaches
        // the same click handler and analytics capture as a pointer pick.
        const option = activeIndex >= 0 ? document.getElementById(optionId(activeIndex)) : null;
        if (option) option.click();
        else closeList();
        return;
      }
      case 'Escape':
        event.preventDefault();
        closeList();
        return;
      case 'Tab':
        // Focus moves on as usual; the value is left as it was.
        closeList();
        return;
      default:
        if (isTypeaheadKey(event)) {
          event.preventDefault();
          setActiveIndex((index) => selectTypeaheadIndex(options, index, event.key));
        }
    }
  };

  const fieldStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: SELECT_SHELL_GAP,
    width: '100%',
    height: SELECT_HEIGHT,
    boxSizing: 'border-box',
    margin: 0,
    paddingLeft: SELECT_HORIZONTAL_PADDING,
    paddingRight: SELECT_HORIZONTAL_PADDING,
    backgroundColor: colors.background,
    border: `${SELECT_BORDER_WIDTH}px solid ${colors.border}`,
    borderRadius: SELECT_BORDER_RADIUS,
    textAlign: 'left',
    cursor: disabled ? 'not-allowed' : 'pointer',
  };

  const textColumnStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: SELECT_LABEL_VALUE_GAP,
    flexGrow: 1,
    minWidth: 0,
    overflow: 'hidden',
  };

  const panelStyle: React.CSSProperties = {
    position: 'absolute',
    top: `calc(100% + ${SELECT_PANEL_GAP}px)`,
    left: 0,
    right: 0,
    zIndex: SELECT_PANEL_LAYER,
    listStyle: 'none',
    margin: 0,
    padding: 0,
    maxHeight: WEB_SELECT_PANEL_MAX_HEIGHT,
    overflowX: 'hidden',
    overflowY: 'auto',
    boxSizing: 'border-box',
    background: SELECT_PANEL_BACKGROUND,
    border: `${SELECT_PANEL_BORDER_WIDTH}px solid ${SELECT_PANEL_BORDER_COLOR}`,
    borderRadius: SELECT_PANEL_RADIUS,
    boxShadow: PANEL_SHADOW,
  };

  return (
    <div ref={rootRef} data-testid={testId ? `${testId}-container` : undefined}>
      <div style={{ position: 'relative' }}>
        <button
          ref={buttonRef}
          type="button"
          role="combobox"
          data-galapagos-select-field="true"
          // Opening the list has its own action; only a pick is a change.
          data-analytics-action={toggleActionId}
          style={fieldStyle}
          autoFocus={autoFocus}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
          aria-label={ariaLabel || undefined}
          aria-labelledby={!ariaLabel && label ? labelId : undefined}
          aria-invalid={hasError}
          aria-disabled={disabled || undefined}
          aria-describedby={hasError ? errorId : undefined}
          onClick={() => (open ? closeList() : openList())}
          onKeyDown={handleKeyDown}
          // Some browsers click a button when Space is released; the key-down
          // already acted, so the release must not toggle the list again.
          onKeyUp={(event) => {
            if (event.key === ' ') event.preventDefault();
          }}
          onFocus={() => {
            setFocused(true);
            onFocus?.();
          }}
          onBlur={() => {
            setFocused(false);
            closeList();
            onBlur?.();
          }}
          {...mouseHoverHandlers(setHovered)}
          data-testid={testId}
        >
          <span style={textColumnStyle}>
            <span
              id={labelId}
              style={textStyle(
                floating ? SELECT_FLOATING_LABEL_TYPOGRAPHY : SELECT_RESTING_LABEL_TYPOGRAPHY,
                colors.label,
              )}
              data-testid={testId ? `${testId}-label` : undefined}
            >
              {label}
            </span>
            {picked ? (
              <span
                style={{
                  ...textStyle(SELECT_VALUE_TYPOGRAPHY, colors.value),
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                data-testid={testId ? `${testId}-value` : undefined}
              >
                {picked.label}
              </span>
            ) : null}
          </span>
          <Icon
            name={SELECT_CHEVRON_ICON}
            size={SELECT_CHEVRON_SIZE}
            color={SELECT_CHEVRON_COLOR}
            aria-hidden
          />
        </button>
        <ul
          role="listbox"
          id={listboxId}
          hidden={!open}
          aria-labelledby={label ? labelId : undefined}
          aria-label={!label && ariaLabel ? ariaLabel : undefined}
          style={panelStyle}
          // Keeps focus on the button when an option is pressed.
          onMouseDown={(event) => event.preventDefault()}
          data-testid={testId ? `${testId}-listbox` : undefined}
        >
          {(open ? options : []).map((option, index) => (
            // An option has no interactive descendants: the row takes the
            // click, and keys reach it through the button's activedescendant.
            <li
              // Position-keyed, so two options sharing a value cannot collide.
              key={`${index}:${option.value}`}
              id={optionId(index)}
              role="option"
              data-analytics-action={actionId}
              aria-selected={option.value === currentValue}
              onClick={() => pick(index)}
              onPointerEnter={(event) => {
                if (event.pointerType === 'mouse') setActiveIndex(index);
              }}
              style={{
                ...textStyle(SELECT_OPTION_TYPOGRAPHY, SELECT_OPTION_TEXT_COLOR),
                padding: `${SELECT_OPTION_VERTICAL_PADDING}px ${SELECT_OPTION_HORIZONTAL_PADDING}px`,
                background:
                  index === activeIndex
                    ? SELECT_OPTION_HIGHLIGHT_BACKGROUND
                    : SELECT_OPTION_BACKGROUND,
                cursor: 'pointer',
              }}
              data-testid={testId ? `${testId}-option-${index}` : undefined}
            >
              {option.label}
            </li>
          ))}
        </ul>
      </div>
      {/* analytics-exempt: non-user-control (a hidden form value, not a control) */}
      {name ? <input type="hidden" name={name} value={currentValue ?? ''} /> : null}
      {/* Always present, so the error is announced when it appears inside it. */}
      <div aria-live="polite" data-testid={testId ? `${testId}-live` : undefined}>
        {hasError ? (
          <div style={{ paddingTop: SELECT_ERROR_GAP, paddingLeft: SELECT_ERROR_INDENT }}>
            <FieldError
              live={false}
              message={errorText}
              id={errorId}
              data-testid={testId ? `${testId}-error` : undefined}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
